/**
 * ErrorBoundary.jsx
 *
 * Root-level React error boundary.
 *
 * - In development (NODE_ENV !== 'production'): shows full error stack.
 * - In production: shows a friendly message without exposing internals.
 * - "Retry" button resets internal state so React re-mounts children.
 * - Error logging hook: calls logError(error, errorInfo) which is a no-op
 *   today but will forward to Sentry once sentry-react is installed.
 */

import { Component } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

// ── Error logger (Sentry-ready placeholder) ──────────────────────────────────
function logError(error, errorInfo) {
  // TODO: replace with Sentry.captureException(error, { extra: errorInfo })
  //       once @sentry/react is installed.
  if (process.env.NODE_ENV === 'development') {
    console.error('[ErrorBoundary]', error, errorInfo);
  }
}

// ── Fallback UI ───────────────────────────────────────────────────────────────
function FallbackUI({ error, onRetry }) {
  const isDev = process.env.NODE_ENV !== 'production';

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)] p-6">
      <div className="card-clay max-w-md w-full p-8 text-center space-y-5">
        <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto">
          <AlertTriangle size={32} className="text-rose-500" strokeWidth={2} />
        </div>

        <div className="space-y-2">
          <h2 className="font-display text-xl font-bold text-[var(--text-main)]">
            Bir şeyler ters gitti
          </h2>
          <p className="text-sm text-[var(--text-muted)]">
            Beklenmedik bir hata oluştu. Lütfen sayfayı yenilemeyi deneyin.
          </p>
        </div>

        {isDev && error && (
          <details className="text-left bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs overflow-auto max-h-48">
            <summary className="font-bold text-rose-600 cursor-pointer mb-2">
              {error.toString()}
            </summary>
            <pre className="text-gray-600 whitespace-pre-wrap">
              {error.stack}
            </pre>
          </details>
        )}

        <div className="flex gap-3 justify-center">
          <button
            onClick={onRetry}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white font-bold text-sm hover:opacity-90 transition-opacity"
            data-testid="error-boundary-retry"
          >
            <RotateCcw size={16} />
            Tekrar Dene
          </button>
          <button
            onClick={() => (window.location.href = '/')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-[var(--text-main)] font-bold text-sm hover:bg-gray-50 transition-colors"
          >
            Ana Sayfa
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Compact block-level fallback ─────────────────────────────────────────────
// Sayfa geneli (min-h-screen) DEĞİL, tek bir blok/panel'in yerine geçer —
// bkz. proje-yoneticisi görev talimatı 2026-08-13, Kusur 2: bir bloktaki
// render hatası diğer blokları (ve sayfanın geri kalanını) etkilememeli.
// `tr` prop'u sayfadan (yerel `tr(trText, enText)` deseni) geçirilir.
export function BlockErrorFallback({ onRetry, tr }) {
  const trFn = typeof tr === 'function' ? tr : (t) => t;
  return (
    <div className="p-6 text-center bg-white rounded-2xl border-2 border-rose-200">
      <AlertTriangle size={24} className="text-rose-500 mx-auto mb-2" strokeWidth={2} />
      <p className="text-sm font-semibold text-[var(--text-main)] mb-3">
        {trFn('Bu bölüm yüklenemedi.', 'This section could not be loaded.')}
      </p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--brand-primary)] text-white font-bold text-sm hover:opacity-90 transition-opacity"
        data-testid="block-error-retry"
      >
        <RotateCcw size={14} />
        {trFn('Tekrar dene', 'Retry')}
      </button>
    </div>
  );
}

// ── Class component ───────────────────────────────────────────────────────────
// `fallback` prop opsiyoneldir: geçirilirse `(error, retry) => ReactNode`
// imzasıyla çağrılır (bkz. BlockErrorFallback kullanımı, AdminDanismanCockpit.jsx
// / AdminDanismanAnaSayfa.jsx). Geçirilmezse mevcut tam sayfa FallbackUI
// davranışı DEĞİŞMEDEN korunur (root-level kullanım için).
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logError(error, errorInfo);
  }

  handleRetry() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (this.state.hasError) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error, this.handleRetry);
      }
      return (
        <FallbackUI
          error={this.state.error}
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}
